import { ActiveDocumentUpload } from '@/apiTypes/SkillInferenceTypes.ts';

const ONE_MB = 1_048_576;
const MIN_DURATION_MS = 60_000;
const MAX_DURATION_MS = 120_000;
const PROGRESS_FLOOR = 10;
const PROGRESS_CEILING = 95;
const PROGRESS_EXPONENT = 1.5;

export const computeFakeProgressDurationMs = (fileSizeBytes: number): number => {
    if (fileSizeBytes <= 0) return MIN_DURATION_MS;
    if (fileSizeBytes >= ONE_MB) return MAX_DURATION_MS;

    const t = fileSizeBytes / ONE_MB;

    return MIN_DURATION_MS + t * (MAX_DURATION_MS - MIN_DURATION_MS);
};

export const computeFakeProgress = (activeJob: ActiveDocumentUpload | null | undefined): number => {
    if (!activeJob?.createdAt || activeJob.fileSize == null) return PROGRESS_FLOOR;

    // updatedAt marks the start of the current extraction attempt (the backend re-drives orphaned
    // jobs), so the bar restarts per attempt instead of sitting at the ceiling through recovery.
    const attemptStartedAt = activeJob.updatedAt ?? activeJob.createdAt;
    const duration = computeFakeProgressDurationMs(activeJob.fileSize);
    const elapsed = Math.max(0, Date.now() - new Date(attemptStartedAt).getTime());
    const t = Math.min(1, elapsed / duration);
    const accelerated = PROGRESS_FLOOR + (PROGRESS_CEILING - PROGRESS_FLOOR) * t ** PROGRESS_EXPONENT;

    return Math.max(PROGRESS_FLOOR, Math.min(PROGRESS_CEILING, accelerated));
};

// Backend absolute deadline is 15 min. Retry fails any job past it, so this guard only has to
// fire no earlier than the backend's; the extra 2 min absorbs browser/server clock drift.
const STALL_DEADLINE_MS = 17 * 60_000;

export const isJobStalled = (activeJob: ActiveDocumentUpload | null | undefined): boolean => {
    if (!activeJob?.createdAt) return false;
    if (activeJob.status !== 'queued' && activeJob.status !== 'processing') return false;

    return Date.now() - new Date(activeJob.runStartedAt ?? activeJob.createdAt).getTime() > STALL_DEADLINE_MS;
};
